import React, { useState, useRef, useEffect } from 'react';
import { useWorkspace } from '@/context/WorkspaceContext';
import { ChevronDown, Plus, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

interface WorkspaceSwitcherPopupProps {
  isGlobalDarkMode: boolean;
  showIcon?: boolean;
  triggerClassName?: string;
  asLogo?: boolean;
  isExpanded?: boolean;
}

export const WorkspaceSwitcherPopup: React.FC<WorkspaceSwitcherPopupProps> = ({ 
  isGlobalDarkMode, 
  showIcon = true,
  triggerClassName,
  asLogo = false,
  isExpanded
}) => {
  const { currentWorkspace, workspaces, setCurrentWorkspace, loading } = useWorkspace();
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowPopup(false);
      }
    };

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopup]);

  if (loading || !currentWorkspace) {
    if (asLogo) {
      return (
        <div className={cn(
          "animate-pulse flex items-center justify-center p-0.5",
          triggerClassName
        )}>
          <div className={cn(
            "w-7 h-7 rounded-lg",
            isGlobalDarkMode ? 'bg-gray-800' : 'bg-gray-200'
          )} />
        </div>
      );
    }
    return (
      <div className={`inline-flex items-center px-3 py-1.5 rounded-lg ${
        isGlobalDarkMode ? 'bg-gray-800/50' : 'bg-gray-100/50'
      } animate-pulse`}>
        <div className={`h-5 w-24 ${isGlobalDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded`}></div>
      </div>
    );
  }

  const handleWorkspaceChange = async (workspace: any) => {
    console.log('[WorkspaceSwitcherPopup] Switching to workspace:', workspace.id, workspace.name);
    setShowPopup(false);
    
    // Update workspace context (this will trigger re-renders in components that depend on it)
    await setCurrentWorkspace(workspace);
    
    // Preload file manager data in the background for faster navigation
    import('@/components/file-manager/utils/fileManagerPreloader').then(({ fileManagerPreloader }) => {
      fileManagerPreloader.preload(workspace.id).catch(console.error);
    });
    
    console.log('[WorkspaceSwitcherPopup] Workspace switched seamlessly');
  };

  const handleCreateWorkspace = () => {
    setShowPopup(false);
    navigate('?modal=settings&tab=workspace');
  };

  return (
    <div className="relative w-full h-full flex items-center">
      <button
        ref={buttonRef}
        onClick={() => setShowPopup(!showPopup)}
        className={cn(
          "w-full h-full flex items-center text-left transition-colors cursor-pointer overflow-hidden",
          isGlobalDarkMode ? "hover:bg-sidebar-accent/50 text-foreground" : "hover:bg-sidebar-light-hover text-gray-900",
          triggerClassName
        )}
        title="Switch workspace"
      >
        {isExpanded !== undefined ? (
          <>
            {/* Fixed Logo Box - exactly 64px wide (w-16), centered logo */}
            <div className="w-16 h-full flex items-center justify-center flex-shrink-0">
              <img
                src={currentWorkspace?.favicon || (isGlobalDarkMode ? "/icon-white.svg" : "/icon-blue.svg")}
                alt={currentWorkspace?.favicon ? currentWorkspace.name : "AgencyPlus"}
                className="w-7 h-7 object-contain"
                onError={(e) => {
                  e.currentTarget.src = isGlobalDarkMode ? "/icon-white.svg" : "/icon-blue.svg";
                }}
              />
            </div>

            {/* Collapsible Name and Chevron - smoothly fades/slides without shifting logo */}
            <div
              className={cn(
                "flex items-center justify-between min-w-0 flex-1 pr-3.5 transition-all duration-300 ease-in-out",
                isExpanded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-3 pointer-events-none"
              )}
            >
              <span className="text-sm font-semibold truncate text-foreground flex-1">
                {currentWorkspace?.name}
              </span>
              <ChevronDown className={cn("w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ml-1", showPopup && "rotate-180")} />
            </div>
          </>
        ) : asLogo ? (
          <img
            src={currentWorkspace?.favicon || (isGlobalDarkMode ? "/icon-white.svg" : "/icon-blue.svg")}
            alt={currentWorkspace?.favicon ? currentWorkspace.name : "AgencyPlus"}
            className="w-7 h-7 object-contain"
            onError={(e) => {
              e.currentTarget.src = isGlobalDarkMode ? "/icon-white.svg" : "/icon-blue.svg";
            }}
          />
        ) : (
          <>
            {showIcon && (
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                {currentWorkspace.favicon && (
                  <img 
                    src={currentWorkspace.favicon} 
                    alt={currentWorkspace.name} 
                    className="w-5 h-5 object-contain" 
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const next = e.currentTarget.nextElementSibling as HTMLElement;
                      if (next) next.style.display = 'flex';
                    }}
                  />
                )}
                <div 
                  className={`w-5 h-5 rounded items-center justify-center ${
                    currentWorkspace.color ? '' : isGlobalDarkMode ? 'bg-gray-700' : 'bg-gray-300'
                  }`} 
                  style={{ 
                    backgroundColor: currentWorkspace.color || undefined,
                    display: currentWorkspace.favicon ? 'none' : 'flex'
                  }}
                >
                  {currentWorkspace.logo ? (
                    <img src={currentWorkspace.logo} alt={currentWorkspace.name} className="w-3.5 h-3.5 object-contain" />
                  ) : (
                    <Building2 className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
            )}
            <span className={showIcon ? 'text-sm font-medium max-w-[120px] truncate' : 'text-base font-semibold max-w-[200px] truncate'}>
              {currentWorkspace.name}
            </span>
            <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${
              showPopup ? 'rotate-180' : ''
            }`} />
          </>
        )}
      </button>

      {showPopup && (
        <div
          ref={popupRef}
          className={cn(
            "absolute top-full mt-1.5 rounded-xl shadow-2xl border z-50 max-h-96 overflow-y-auto",
            isExpanded ? "left-3 right-3 w-auto" : "left-3 w-72",
            isGlobalDarkMode
              ? 'bg-sidebar-accent border-sidebar-border'
              : 'bg-white border-gray-200'
          )}
        >
          <div className={cn("p-3 border-b flex items-center justify-between", isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200')}>
            <p className={cn("text-xs font-semibold px-2", isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500')}>
              Switch Workspace
            </p>
            <button
              onClick={handleCreateWorkspace}
              className={cn(
                "p-1.5 rounded-lg transition-colors",
                isGlobalDarkMode
                  ? 'hover:bg-sidebar text-sidebar-foreground/60 hover:text-sidebar-foreground'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              )}
              title="Create Workspace"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="p-2">
            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => handleWorkspaceChange(workspace)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  currentWorkspace.id === workspace.id
                    ? isGlobalDarkMode
                      ? 'bg-nav-active text-white'
                      : 'bg-blue-50 text-blue-700'
                    : isGlobalDarkMode
                      ? 'hover:bg-sidebar text-gray-300'
                      : 'hover:bg-gray-100 text-gray-700'
                )}
              >
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-md overflow-hidden">
                  {workspace.favicon && (
                    <img 
                      src={workspace.favicon} 
                      alt={workspace.name} 
                      className="w-6 h-6 object-contain" 
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const next = e.currentTarget.nextElementSibling as HTMLElement;
                        if (next) next.style.display = 'flex';
                      }}
                    />
                  )}
                  <div 
                    className={`w-6 h-6 rounded-md items-center justify-center ${
                      workspace.color ? '' : isGlobalDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                    }`} 
                    style={{ 
                      backgroundColor: workspace.color || undefined,
                      display: workspace.favicon ? 'none' : 'flex'
                    }}
                  >
                    {workspace.logo ? (
                      <img src={workspace.logo} alt={workspace.name} className="w-3.5 h-3.5 object-contain" />
                    ) : (
                      <Building2 className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate">
                    {workspace.name}
                  </p>
                </div>
                {currentWorkspace.id === workspace.id && (
                  <div 
                    className="w-2 h-2 rounded-full flex-shrink-0 ring-2 ring-white/80 dark:ring-sidebar-accent"
                    style={{ backgroundColor: workspace.color || (isGlobalDarkMode ? '#60a5fa' : '#2563eb') }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


